import { keyName, type Analysis } from './analyze';
import { catOf, type PartLookup } from './catalog';
import { t as $t } from './i18n';
import { plcChannels, plcTags, type PlcChannel, type PlcKind } from './plc';
import type { Project } from './types';

/*
 * PLC hardware and I/O as AutomationML (CAEX 2.15) after the Application Recommendation "Automation Project
 * Configuration" (AR APC 1.4.0), the format TIA Portal and EPLAN exchange PLC data in. The structure follows a
 * file TIA Portal writes itself: project → station (Device) → rack → CPU with its tag table, and the I/O cards with
 * their order number, slot, address ranges and channels; every channel is linked to its tag.
 *
 * EnerCAD draws no CPU, so its order number and firmware come from the caller. Cards are identified by their part
 * number (TIA Portal finds the module by order number); a card without one is written without it and reported.
 */

export interface AmlOptions {
  /** station name (the Device) */
  station: string;
  /** "s71500": station and rack as TIA Portal writes them for S7-1500; "generic": CPU and cards right under the station */
  system: 's71500' | 'generic';
  cpuName: string;
  /** CPU order number, e.g. "6ES7 511-1AK02-0AB0" */
  cpuOrder: string;
  cpuFirmware?: string;
  /** writing time (ISO) */
  now: string;
}

const xe = (s: string | number) =>
  String(s).replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** A stable UUID from a text (four FNV-1a hashes): the same project gives the same IDs on every export. */
export function stableUuid(s: string): string {
  const h = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b].map((seed) => {
    let x = seed >>> 0;
    for (let i = 0; i < s.length; i++) x = Math.imul(x ^ s.charCodeAt(i), 0x01000193) >>> 0;
    return x.toString(16).padStart(8, '0');
  }).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-${((parseInt(h[16], 16) & 3) | 8).toString(16)}${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

const attr = (name: string, type: string, value: string | number | boolean) => `<Attribute Name="${name}" AttributeDataType="${type}"><Value>${xe(String(value))}</Value></Attribute>`;
const str = (name: string, v: string | undefined) => (v ? attr(name, 'xs:string', v) : '');
const role = (r: string) => `<SupportedRoleClass RefRoleClassPath="AutomationProjectConfigurationRoleClassLib/${r}" />`;

const DIGITAL = (k: PlcKind) => k === 'DI' || k === 'DQ';
const INPUT = (k: PlcKind) => k === 'DI' || k === 'AI';

export interface AmlResult {
  xml: string;
  /** cards, channels and tags written */
  cards: number;
  channels: number;
  warnings: string[];
}

/** The PLC of the project as an AutomationML file (AR APC 1.4.0). */
export function plcAutomationML(pr: Project, A: Analysis, o: AmlOptions, parts: PartLookup = catOf): AmlResult {
  const warnings: string[] = [];
  const id = (k: string) => stableUuid(`${pr.name}|${o.station}|${k}`);
  // generator controllers are not PLC cards (their channels are the controller's own terminals)
  const all = plcChannels(A).filter((c) => A.mains.get(c.key)?.[0]?.s.type !== 'gctl');
  const tagOf = new Map(plcTags(A).map((t) => [t.c.it.s.id, t]));
  const cards = new Map<string, PlcChannel[]>();
  for (const c of all) {
    if (!c.parsed) {
      warnings.push($t('{0}: "{1}" tanınan bir PLC adresi değil; kanal yazılmadı', keyName(A, c.key), c.addr || $t('(boş)')));
      continue;
    }
    (cards.get(c.key) ?? cards.set(c.key, []).get(c.key)!).push(c);
  }
  const keys = [...cards.keys()].sort((a, b) => keyName(A, a).localeCompare(keyName(A, b), 'tr', { numeric: true }));

  const links: string[] = [];
  let channels = 0;
  // the cards from slot 2 on (slot 1: CPU)
  const cardXml = keys.map((key, i) => {
    const list = cards.get(key)!;
    const s = list.find((c) => c.it.s.part)?.it.s ?? list[0].it.s;
    const part = s.part ? parts(s.part) : undefined;
    const name = keyName(A, key);
    const cardId = id('card|' + key);
    if (!s.part) warnings.push($t('{0}: parça numarası yok; TIA Portal kartı sipariş numarasından tanır', name));
    // address ranges per direction: from the lowest byte the card uses, as long as its channels (or its part) need
    const ranges: string[] = [], chans: string[] = [];
    for (const kind of ['DI', 'DQ', 'AI', 'AQ'] as PlcKind[]) {
      const own = list.filter((c) => c.kind === kind);
      if (!own.length) continue;
      const dig = DIGITAL(kind), io = INPUT(kind) ? 'Input' : 'Output';
      const start = Math.min(...own.map((c) => c.parsed!.byte));
      const num = (c: PlcChannel) => (dig ? (c.parsed!.byte - start) * 8 + c.parsed!.bit : (c.parsed!.byte - start) / 2);
      const top = Math.max(...own.map(num)) + 1;
      const count = Math.max(top, own.length === list.length && part?.ch ? part.ch : 0);
      const length = dig ? Math.ceil(count / 8) * 8 : count * 16;
      ranges.push(`<Attribute Name="${ranges.length + 1}">${attr('StartAddress', 'xs:int', start)}${attr('Length', 'xs:int', length)}${attr('IoType', 'xs:string', io)}</Attribute>`);
      for (const c of own.sort((a, b) => num(a) - num(b))) {
        const n = num(c);
        if (!Number.isInteger(n)) {
          warnings.push($t('{0}: {1} analog kanal adresi çift baytta değil; kanal yazılmadı', name, c.addr));
          continue;
        }
        const ch = `Channel_${kind === 'DQ' ? 'DO' : kind === 'AQ' ? 'AO' : kind}_${n}`;
        chans.push(`<ExternalInterface ID="${id('ch|' + c.it.s.id)}" Name="${ch}" RefBaseClassPath="AutomationProjectConfigurationInterfaceClassLib/Channel">${attr('Type', 'xs:string', dig ? 'Digital' : 'Analog')}${attr('IoType', 'xs:string', io)}${attr('Number', 'xs:int', n)}${attr('Length', 'xs:int', dig ? 1 : 16)}</ExternalInterface>`);
        const tag = tagOf.get(c.it.s.id);
        if (tag) links.push(`<InternalLink Name="Link To ${xe(tag.name)}" RefPartnerSideA="${cardId}:${ch}" RefPartnerSideB="${id('tagtable')}:${xe(tag.name)}" />`);
        channels++;
      }
    }
    const [fn, site, place] = [s.fn ?? pr.fn, s.site ?? pr.site, s.loc ?? pr.loc];
    return `<InternalElement ID="${cardId}" Name="${xe(name.replace(/^[-=+]+/, '').slice(0, 100) || 'Card')}">
${str('TypeName', part?.d ?? '')}${attr('PositionNumber', 'xs:int', i + 2)}${attr('BuiltIn', 'xs:boolean', false)}${s.part ? attr('TypeIdentifier', 'xs:string', 'OrderNumber:' + s.part) : ''}${str('Manufacturer', s.maker || part?.maker)}${str('Comment', s.desc)}
${fn ? attr('PlantDesignation IEC', 'xs:string', '=' + fn) : ''}${place || site ? attr('LocationIdentifier IEC', 'xs:string', (site ? '++' + site : '') + (place ? '+' + place : '')) : ''}${attr('ProductDesignation IEC', 'xs:string', '-' + (s.tag || name.replace(/^.*-/, '')))}
<Attribute Name="Address"><RefSemantic CorrespondingAttributePath="OrderedListType" />${ranges.join('')}</Attribute>
${chans.join('\n')}
${role('DeviceItem')}
</InternalElement>`;
  });

  const tags = [...tagOf.values()].filter((t) => cards.has(t.c.key) && t.c.parsed);
  const tagXml = tags.map((t) => {
    const p = t.c.parsed!;
    // TIA Portal writes the address without the direction: "0.0" for a bit, "W64" for a word
    const addr = p.word ? `W${p.byte}` : `${p.byte}.${p.bit}`;
    return `<ExternalInterface ID="${id('tag|' + t.c.it.s.id)}" Name="${xe(t.name)}" RefBaseClassPath="AutomationProjectConfigurationInterfaceClassLib/Tag">${attr('DataType', 'xs:string', t.type)}${attr('IoType', 'xs:string', INPUT(t.c.kind) ? 'Input' : 'Output')}${attr('LogicalAddress', 'xs:string', addr)}${str('Comment', t.comment)}</ExternalInterface>`;
  });
  if (!o.cpuOrder) warnings.push($t('CPU sipariş numarası verilmedi; TIA Portal CPU\'yu tanımayabilir'));
  const cpu = `<InternalElement ID="${id('cpu')}" Name="${xe(o.cpuName || 'PLC_1')}">
${attr('DeviceItemType', 'xs:string', 'CPU')}${attr('PositionNumber', 'xs:int', 1)}${attr('BuiltIn', 'xs:boolean', false)}${o.cpuOrder ? attr('TypeIdentifier', 'xs:string', 'OrderNumber:' + o.cpuOrder) : ''}${str('FirmwareVersion', o.cpuFirmware)}
<InternalElement ID="${id('tagtable')}" Name="Default tag table">
${tagXml.join('\n')}
${role('TagTable')}
</InternalElement>
${role('DeviceItem')}
</InternalElement>`;
  const items = [cpu, ...cardXml].join('\n');
  const station =
    o.system === 's71500'
      ? `<InternalElement ID="${id('station')}" Name="${xe(o.station)}">
${attr('TypeIdentifier', 'xs:string', 'System:Device.S71500')}
<InternalElement ID="${id('rack')}" Name="Rack_0">
${attr('TypeName', 'xs:string', 'Rack')}${attr('PositionNumber', 'xs:int', 0)}${attr('BuiltIn', 'xs:boolean', false)}${attr('TypeIdentifier', 'xs:string', 'OrderNumber:6ES7 590-1***0-0AA0')}
${items}
${role('DeviceItem')}
${links.join('\n')}
</InternalElement>
${role('Device')}
</InternalElement>`
      : `<InternalElement ID="${id('station')}" Name="${xe(o.station)}">
${items}
${role('Device')}
${links.join('\n')}
</InternalElement>`;
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<CAEXFile xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" FileName="${xe(o.station)}.aml" SchemaVersion="2.15" xsi:noNamespaceSchemaLocation="CAEX_ClassModel_V2.15.xsd">
<AdditionalInformation>
<WriterHeader>
<WriterName>EnerCAD</WriterName>
<WriterID>EnerCAD</WriterID>
<WriterVendor>Enerarge</WriterVendor>
<WriterVendorURL>https://enercontrols.com</WriterVendorURL>
<WriterVersion>1.0</WriterVersion>
<WriterRelease>1.0</WriterRelease>
<LastWritingDateTime>${xe(o.now)}</LastWritingDateTime>
</WriterHeader>
</AdditionalInformation>
<AdditionalInformation AutomationMLVersion="2.0" />
<AdditionalInformation DocumentVersions="Recommendations">
<Document DocumentIdentifier="AR APC" Version="1.4.0" />
</AdditionalInformation>
<InstanceHierarchy Name="${xe(pr.name || 'EnerCAD')}">
<InternalElement ID="${id('project')}" Name="${xe(pr.name || 'EnerCAD')}">
${station}
${role('AutomationProject')}
</InternalElement>
</InstanceHierarchy>
</CAEXFile>
`;
  return { xml, cards: keys.length, channels, warnings };
}
